import { useState, useEffect, useMemo } from "react";
import { Bug, ImageOff, HelpCircle, AlertTriangle, Loader2 } from "lucide-react";
import { fetchDebugCards } from "../lib/api";
import { Button } from "./ui/button";

// Étiquettes des trois problèmes détectés côté backend (voir /debug/cards).
const ISSUES = {
  image: {
    label: "Sans image", icon: ImageOff, color: "text-orange-400",
    help: "Aucune illustration trouvée : ni scan FR TCGdex, ni Pokécardex, ni image anglaise. La carte s'affiche en placeholder.",
  },
  variants: {
    label: "Variants inconnus", icon: HelpCircle, color: "text-sky-400",
    help: "TCGdex ne renvoie aucun variant pour cette carte : la grille affiche un badge « … » au lieu d'une info fausse.",
  },
  variants_doubt: {
    label: "Variants douteux", icon: AlertTriangle, color: "text-amber-400",
    help: "Seul le variant normal est déclaré, et la rareté ne permet pas d'en déduire un Reverse. Souvent un trou de données sur les anciens blocs — à trancher avec un override manuel depuis la fiche.",
  },
};

// Vue « Diagnostic » : liste les cartes mal renseignées pour les corriger.
// Ne voit que les cartes déjà en cache, donc la liste s'étoffe au fil des sets
// parcourus — c'est assumé (cf. commentaire de l'endpoint).
export const DebugView = ({ onSelectCard }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [issue, setIssue] = useState("image");

  useEffect(() => {
    setLoading(true);
    fetchDebugCards({ issue })
      .then(setData)
      .catch(err => console.error("Diagnostic :", err))
      .finally(() => setLoading(false));
  }, [issue]);

  // Regroupement par set pour parcourir les problèmes set par set.
  const bySet = useMemo(() => {
    const groups = new Map();
    (data?.cards || []).forEach(c => {
      const key = c.set_id;
      if (!groups.has(key)) groups.set(key, { id: key, name: c.set_name || key, cards: [] });
      groups.get(key).cards.push(c);
    });
    return [...groups.values()];
  }, [data]);

  const counts = data?.counts || {};

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-2">
        <Bug className="w-6 h-6 text-red-400" />
        <h2 className="text-2xl font-bold text-white">Diagnostic des cartes</h2>
      </div>
      <p className="text-sm text-zinc-500 mb-5">
        {data
          ? `${data.scanned.toLocaleString("fr-FR")} cartes analysées (celles déjà consultées au moins une fois).`
          : "Analyse en cours…"}
      </p>

      <div className="flex flex-wrap gap-2 mb-6">
        {Object.entries(ISSUES).map(([key, meta]) => {
          const Icon = meta.icon;
          const active = issue === key;
          return (
            <Button key={key} onClick={() => setIssue(key)}
              className={`border ${active
                ? "bg-zinc-700 border-zinc-500 text-white"
                : "bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700"}`}>
              <Icon className={`w-4 h-4 mr-2 ${meta.color}`} />
              {meta.label}
              <span className="ml-2 text-xs text-zinc-400">{counts[key] ?? "…"}</span>
            </Button>
          );
        })}
      </div>

      <p className="text-sm text-zinc-400 bg-zinc-900/60 border border-zinc-800 rounded-lg p-3 mb-6">
        {ISSUES[issue].help}
      </p>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-red-500" />
        </div>
      ) : !bySet.length ? (
        <p className="text-zinc-500 py-10 text-center">Aucune carte concernée. 🎉</p>
      ) : (
        <>
          {data.truncated && (
            <p className="text-xs text-amber-400 mb-3">
              Liste tronquée : {data.total.toLocaleString("fr-FR")} cartes concernées,
              seules les premières sont affichées.
            </p>
          )}
          <div className="space-y-5">
            {bySet.map(group => (
              <div key={group.id}>
                <h3 className="text-sm font-semibold text-zinc-300 mb-2">
                  {group.name}
                  <span className="ml-2 text-xs font-normal text-zinc-600">
                    {group.cards.length} carte{group.cards.length > 1 ? "s" : ""}
                  </span>
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {group.cards.map(card => (
                    <button key={card.id} onClick={() => onSelectCard(card.id)}
                      title={`${card.name} — ${card.rarity || "rareté inconnue"}`}
                      className="text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700 rounded px-2 py-1">
                      <span className="text-zinc-500 mr-1">{card.localId}</span>
                      {card.name}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
